/**
 * Tenant subscriptions list, pending rows, subscribe and detail (TMMPDM-0008).
 * TMMPDM-0035: the status and email filters live in the URL; sticky headers; filtered-empty Clear.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import { oneOf, text, useSearchDraft, useUrlState, type UrlSchema } from '@/hooks/useUrlState';
import { EmptyState, ErrorState, useNotify } from '@/components/page';
import { stickyTableContainerSx } from '@/components/ui';
import { FilterTabs } from '@/components/ui/FilterTabs';
import { adminApi } from '@/api/tmm/admin';
import type {
  AdminSubscribeResponse,
  AdminSubscriptionRow,
  OfferView,
  PendingSubscriptionRow,
  TenantView,
} from '@/api/tmm/types';
import { SubscribeDialog } from './SubscribeDialog';
import { SubscriptionDetailDialog } from './SubscriptionDetailDialog';
import { formatDate } from '@/format';
import { StatusChip } from '@/status';

interface SubscriptionsPanelProps {
  tenant: TenantView;
}

/**
 * TMMPDM-0035: the tab's view lives in the URL beside `?tab=` — `status` (a status the server
 * defines; an unknown one simply matches nothing) and `q`, the email search.
 * TMMPDM-0100: `view` splits Subscriptions and Pending. `tab` still names the partner leaf.
 */
const SUBSCRIPTION_VIEWS = ['subscriptions', 'pending'] as const;
type SubscriptionLeaf = (typeof SUBSCRIPTION_VIEWS)[number];
type SubscriptionsView = { status: string; q: string; view: SubscriptionLeaf };
const VIEW: UrlSchema<SubscriptionsView> = {
  status: { default: '', parse: text },
  q: { default: '', parse: text },
  view: { default: 'subscriptions', parse: oneOf(SUBSCRIPTION_VIEWS) },
};

function cardLabel(row: AdminSubscriptionRow): string {
  if (!row.cardBrand && !row.maskedCard) return '—';
  if (row.cardBrand && row.maskedCard) return `${row.cardBrand} ····${row.maskedCard}`;
  return row.cardBrand ?? row.maskedCard ?? '—';
}

export function SubscriptionsPanel({ tenant }: SubscriptionsPanelProps) {
  const { t } = useCopy();
  const notify = useNotify();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const slug = tenant.slug;
  const resellerId = tenant.reseller?.id ?? null;
  const resellerName = tenant.reseller?.name ?? null;
  const [reloadToken, setReloadToken] = useState(0);
  const [rows, setRows] = useState<AdminSubscriptionRow[] | null>(null);
  const [pending, setPending] = useState<PendingSubscriptionRow[]>([]);
  const [offers, setOffers] = useState<OfferView[]>([]);
  const [error, setError] = useState<{ cause: unknown } | null>(null);
  const [view, setView] = useUrlState(VIEW);
  const status = view.status;
  const emailQuery = view.q;
  const email = useSearchDraft(emailQuery, (q) => setView({ q }, { history: 'replace' }));
  const [observedStatuses, setObservedStatuses] = useState<string[]>([]);
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const [viewId, setViewId] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    const query = {
      ...(status ? { status } : {}),
      ...(emailQuery ? { email: emailQuery } : {}),
    };
    adminApi
      .listSubscriptions(slug, Object.keys(query).length > 0 ? query : undefined)
      .then((list) => {
        if (cancelled) return;
        setRows(list.subscriptions);
        setPending(list.pending);
        setError(null);
        setObservedStatuses((prev) => {
          const next = new Set(prev);
          for (const row of list.subscriptions) next.add(row.status);
          return [...next];
        });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setRows(null);
          setError({ cause: err });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [slug, status, emailQuery, reloadToken]);

  useEffect(() => {
    let cancelled = false;
    adminApi
      .listOffers(slug)
      .then((list) => {
        if (!cancelled) setOffers(list.offers);
      })
      .catch(() => {
        if (!cancelled) setOffers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // A deep-linked status is offered even before a load has shown it.
  const statusOptions =
    status !== '' && !observedStatuses.includes(status)
      ? [...observedStatuses, status]
      : observedStatuses;
  const filtered = status !== '' || emailQuery !== '';
  const clearFilters = () => {
    email.discard();
    setView({ status: '', q: '' });
  };

  const currentOfferId = useMemo(() => offers.find((offer) => offer.current)?.id, [offers]);

  const offerLabel = (id: number | undefined) => {
    if (id == null) return '—';
    if (currentOfferId != null && id === currentOfferId) {
      return t('subscriptions.offerCurrent', 'current');
    }
    return String(id);
  };

  const onSubscribed = (result: AdminSubscribeResponse) => {
    setSubscribeOpen(false);
    if (result.pending) {
      notify.success(
        t(
          'subscriptions.subscribed.pending',
          'Invitation sent; the subscription starts when {{email}} completes sign-up.',
          { email: result.pending.email },
        ),
      );
    } else {
      notify.success(t('subscriptions.subscribed.created', 'Subscription created.'));
    }
    setReloadToken((n) => n + 1);
  };

  if (error) {
    return (
      <ErrorState
        error={error.cause}
        message={t('subscriptions.loadError', 'Could not load subscriptions.')}
        onRetry={() => setReloadToken((n) => n + 1)}
      />
    );
  }

  const pendingLabel = t('subscriptions.tabs.count', '{{label}} ({{count, number}})', {
    label: t('subscriptions.tabs.pending', 'Pending'),
    count: pending.length,
  });

  return (
    <Stack spacing={2}>
      <FilterTabs
        ariaLabel={t('subscriptions.tabs.aria', 'Subscription sections')}
        active={view.view}
        onChange={(next) => {
          const leaf = oneOf(SUBSCRIPTION_VIEWS)(next);
          if (leaf) setView({ view: leaf });
        }}
        tabs={[
          {
            key: 'subscriptions',
            label: t('subscriptions.tabs.subscriptions', 'Subscriptions'),
          },
          { key: 'pending', label: pendingLabel },
        ]}
      />
      {view.view === 'subscriptions' ? (
        <>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ alignItems: 'center' }}>
            <TextField
              select
              label={t('subscriptions.statusFilter', 'Status')}
              value={status}
              onChange={(event) => setView({ status: event.target.value })}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">{t('subscriptions.statusAll', 'All')}</MenuItem>
              {statusOptions.map((value) => (
                <MenuItem key={value} value={value}>
                  {t(`subscription.status.${value}`, value)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label={t('subscriptions.emailFilter', 'Email')}
              value={email.value}
              onChange={(event) => email.onChange(event.target.value)}
              helperText={t('subscriptions.emailHint', 'Searches email')}
            />
            <Button variant="contained" onClick={() => setSubscribeOpen(true)}>
              {t('subscriptions.subscribe', 'Subscribe')}
            </Button>
          </Stack>

          <TableContainer component={Paper} variant="outlined" sx={stickyTableContainerSx}>
            <Table
              size="small"
              stickyHeader
              aria-label={t('subscriptions.tableAria', 'Tenant subscriptions')}
            >
              <TableHead>
                <TableRow>
                  <TableCell>{t('subscriptions.col.email', 'Email')}</TableCell>
                  <TableCell>{t('subscriptions.col.name', 'Name')}</TableCell>
                  <TableCell>{t('subscriptions.col.status', 'Status')}</TableCell>
                  <TableCell>{t('subscriptions.col.origin', 'Origin')}</TableCell>
                  <TableCell>{t('subscriptions.col.offer', 'Offer')}</TableCell>
                  <TableCell>{t('subscriptions.col.period', 'Period')}</TableCell>
                  <TableCell>{t('subscriptions.col.nextBilling', 'Next billing')}</TableCell>
                  <TableCell>{t('subscriptions.col.card', 'Card')}</TableCell>
                  <TableCell>{t('subscriptions.col.actions', 'Actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows === null ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      {t('common.loading', 'Loading')}
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <EmptyState
                        title={t('subscriptions.empty', 'No subscriptions.')}
                        {...(filtered ? { filtered: { onClear: clearFilters } } : {})}
                      />
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.email}</TableCell>
                      <TableCell>{row.displayName ?? '—'}</TableCell>
                      <TableCell>
                        <StatusChip
                          status={row.status}
                          label={t(`subscription.status.${row.status}`, row.status)}
                        />
                      </TableCell>
                      <TableCell>{t(`subscriptions.origin.${row.origin}`, row.origin)}</TableCell>
                      <TableCell>{offerLabel(row.retailOfferId)}</TableCell>
                      <TableCell>
                        {formatDate(row.currentPeriodStart, locale)} –{' '}
                        {formatDate(row.currentPeriodEnd, locale)}
                      </TableCell>
                      <TableCell>{formatDate(row.nextBillingOn, locale)}</TableCell>
                      <TableCell>{cardLabel(row)}</TableCell>
                      <TableCell>
                        <Button size="small" onClick={() => setViewId(row.id)}>
                          {t('subscriptions.view', 'View')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : pending.length === 0 ? (
        <EmptyState surface title={t('subscriptions.pending.empty', 'No pending subscriptions.')} />
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table
            size="small"
            aria-label={t('subscriptions.pending.title', 'Pending admin subscriptions')}
          >
            <TableHead>
              <TableRow>
                <TableCell>{t('subscriptions.pending.email', 'Email')}</TableCell>
                <TableCell>{t('subscriptions.pending.status', 'Status')}</TableCell>
                <TableCell>{t('subscriptions.pending.offer', 'Offer')}</TableCell>
                <TableCell>{t('subscriptions.pending.trialDays', 'Trial days')}</TableCell>
                <TableCell>{t('subscriptions.pending.compLabel', 'Comp')}</TableCell>
                <TableCell>{t('subscriptions.pending.createdBy', 'Created by')}</TableCell>
                <TableCell>{t('subscriptions.pending.expires', 'Expires')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pending.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.email}</TableCell>
                  <TableCell>
                    {t(`subscriptions.pendingStatus.${row.status}`, row.status)}
                  </TableCell>
                  <TableCell>{row.retailOfferId ?? '—'}</TableCell>
                  <TableCell>{row.trialDays ?? '—'}</TableCell>
                  <TableCell>
                    {row.comp
                      ? t('subscriptions.pending.comp', 'Comp until {{compedUntil}}', {
                          compedUntil: formatDate(row.compedUntil, locale),
                        })
                      : '—'}
                  </TableCell>
                  <TableCell>{row.createdByAdmin ?? '—'}</TableCell>
                  <TableCell>{formatDate(row.expiresAt, locale)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <SubscribeDialog
        open={subscribeOpen}
        slug={slug}
        resellerId={resellerId}
        resellerName={resellerName}
        currency={tenant.currency}
        onClose={() => setSubscribeOpen(false)}
        onDone={onSubscribed}
      />
      {viewId != null ? (
        <SubscriptionDetailDialog
          key={viewId}
          open
          slug={slug}
          subscriptionId={viewId}
          locale={locale}
          resellerId={resellerId}
          resellerName={resellerName}
          currency={tenant.currency}
          onClose={() => setViewId(null)}
          onChanged={() => setReloadToken((n) => n + 1)}
        />
      ) : null}
    </Stack>
  );
}
